import React, { useState, useEffect } from 'react';
import { compose, isEmpty } from 'ramda';
import { CircularProgress } from '@material-ui/core/';
import styled from 'styled-components/macro';
import queryString from 'query-string';
import { RouteComponentProps } from 'react-router-dom';
import { showToast, SurveyInstance, BackTo } from '@deeptrue-frontend/ui';
import { ProjectStatus } from 'consts/statuses';
import { Dashboard } from 'components/Layout';
import StoreProvider from 'components/StoreProvider/StoreProvider';
import { OrganizationType } from 'store/modules/auth/auth';
import { withMetaDescription } from 'components/HeadProvider/HeadProvider';
import { usePaymentsLimits } from 'hooks';
import ProjectMenu from '../Common/ProjectMenu';
import { withProject, WithProjectProps } from '../withProject';
import Sidebar from './Sidebar';
import api from 'api';
import IntlProvider, { useFormatMessage } from 'components/IntlProvider';
import Header from './Header';
import BexioSidebar from 'components/SurveyBriefing/Sidebars/BexioProject';
import CustomerSatisfaction from 'components/SurveyBriefing/Sidebars/CustomerSatisfaction';
import { productsSlugs } from 'consts/products';

const Content = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 5rem;
  align-items: center;
  width: 100%;
  height: 100%;
`;

const ProjectMenuWrapper = styled.div`
  width: calc(100% - 2rem);
  .tab-bar {
    margin-left: -2rem;
    padding-left: 2rem;
    margin-right: -2rem;
    padding-right: 2rem;
  }
`;

const StyledDashboard = styled(Dashboard)`
  width: calc(100%-2rem);
`;

const { connect, selectors, actions } = StoreProvider;

interface ProjectProps
  extends RouteComponentProps<{ id: string; codename: string }> {
  product: WithProjectProps['product'];
  project: WithProjectProps['project'];
  user: any;

  organization: OrganizationType;
  isProjectAccepted: WithProjectProps['isProjectAccepted'];
  isProjectArchived: WithProjectProps['isProjectArchived'];
  isCollectingData: WithProjectProps['isCollectingData'];
  updateProject: (project: WithProjectProps['project']) => void;
  projectIsLoaded: boolean;
  isBexioUser: boolean;
  isBrandValuatorProduct: boolean;
}

const ProjectEdit = ({
  location,
  project,
  product,
  organization,
  match: { params },
  updateProject,
  history,
  projectIsLoaded,
  isBexioUser,
  isBrandValuatorProduct,
  user,
}: ProjectProps) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [initialValues, setInitialValues] = useState(project.json);
  const { available_tokens } = usePaymentsLimits({ organization }).limitsRes;
  const { locale } = IntlProvider.useIntl();
  const t = useFormatMessage();
  const {
    id,
    status,
    surveyUuid,
    is_subscribable,
    subscription,
    product_slug,
    json,
  } = project;

  const discount = Number(json.Bdiscount || 0);
  const totalFee = Number(json.total_fee || 0);
  const totalProjectCostValue = totalFee - discount;
  const hasSufficientCredits =
    project.id !== -1 && available_tokens >= totalProjectCostValue;

  const { card } = queryString.parse(location.search);
  const reportUrl = params.codename
    ? `/admin/organizations/${params.codename}/projects/${project.id}/reports/`
    : `/project/${project.id}/reports/`;
  const collectDataUrl = params.codename
    ? `/admin/organizations/${params.codename}/projects/${project.id}/collect-data/`
    : `/project/${project.id}/collect-data/`;

  const applyVoucher = (
    code: string,
    updateVoucher: () => void,
    failureCallback: () => void
  ) => {
    return api.resources
      .applyVoucher({
        organizationCodename: params.codename || organization.codename,
        projectId: project.id,
        voucherCode: code,
      })
      .then(({ data }: any) => {
        updateProject(data);
        updateVoucher();
      })
      .catch(() => {
        failureCallback();
        showToast({
          type: 'error',
          title: t(
            'errors.toast.general',
            'Something went wrong, please contact administrator'
          ),
        });
      });
  };

  const removeVoucher = (
    updateVoucher: () => void,
    failureCallback: () => void
  ) => {
    return api.resources
      .removeVoucher({
        organizationCodename: params.codename || organization.codename,
        projectId: project.id,
      })
      .then(({ data }: any) => {
        updateProject(data);
        updateVoucher();
      })
      .catch(() => {
        failureCallback();
        showToast({
          type: 'error',
          title: t(
            'errors.toast.general',
            'Something went wrong, please contact administrator'
          ),
        });
      });
  };

  useEffect(() => {
    if (card === 'success') {
      showToast({
        type: 'success',
        title: t(
          'project.customerSatisfaction.payments.card.success',
          'Subscription success.'
        ),
        id: 'card-success',
      });
    }
    if (card === 'failure') {
      showToast({
        type: 'warn',
        title: t(
          'project.customerSatisfaction.payments.card.failure',
          'Subscription failure.'
        ),
        id: 'card-failure',
      });
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (projectIsLoaded) {
      if (status.value === ProjectStatus.OFFER_ARCHIVED) {
        showToast({
          type: 'warn',
          title: t('project.testEdit.toats.archived', 'Project is archived.'),
          id: 'project-archived',
        });
      }
      if (status.value === ProjectStatus.FINISHED) {
        showToast({
          type: 'success',
          title: t(
            'project.testEdit.toats.finished',
            'Your project is already finished.'
          ),
          id: 'project-finished',
        });
      }
      if (status.value === ProjectStatus.COLLECTING_DATA) {
        showToast({
          type: 'collectingData',
          title: t(
            'project.testEdit.toats.collecting',
            'Data collection started. You can still add new respondents to your project. '
          ),
          id: 'collection-started',
          cta: t('project.testEdit.toats.cta', 'Add respondents'),
          onCtaClick: () => history.push(collectDataUrl),
        });
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status.value, projectIsLoaded]);

  useEffect(() => {
    setInitialValues(project.json);
  }, [project.json]);

  const updateProjectStatus = (statusName: 'edit' | 'accept') => {
    setIsProcessing(true);
    api.resources
      .updateProjectStatus({
        organizationCodename: params.codename || organization.codename,
        project: {
          ...project,
          email: user.email,
          name: organization.name,
        },
        status: statusName,
      })
      .then(({ data }) => {
        updateProject(data);
        setIsProcessing(false);
      })
      .catch(() => {
        showToast({
          type: 'error',
          title: t(
            'errors.toast.general',
            'Something went wrong, please contact administrator'
          ),
        });
      });
  };

  const generateReport = () => {
    setIsGeneratingReport(true);

    return api.resources
      .generateBrandValuatorReport({
        organizationCodename: params.codename || organization.codename,
        projectId: project.id,
      })
      .then(() => {
        const updatedProject = {
          ...project,
          status: {
            value: 9,
            display: 'ANALYSIS_DONE',
          },
        };

        updateProject(updatedProject);
        setIsGeneratingReport(false);
        history.push(reportUrl);
      })
      .catch(() => {
        setIsGeneratingReport(false);
      });
  };

  const updateProjectData = (surveyData: any) => {
    setIsProcessing(true);

    api.resources
      .updateProject({
        organizationCodename: params.codename || organization.codename,
        project: {
          ...project,
        },
        surveyData,
      })
      .then(() => {
        updateProjectStatus('accept');
      })
      .catch(() => {
        showToast({
          type: 'error',
          title: t(
            'errors.toast.general',
            'Something went wrong, please contact administrator'
          ),
        });
      });
  };
  const mode = status.value === ProjectStatus.OFFER_DRAFT ? 'edit' : 'display';
  const isJsonEmpty =
    isEmpty(product.briefing_template.json) || isEmpty(initialValues);

  const showPaymentSection = status.value === ProjectStatus.COLLECTING_DATA;

  const areActionsDisabled =
    status.value === ProjectStatus.OFFER_ARCHIVED ||
    status.value === ProjectStatus.FINISHED ||
    status.value === ProjectStatus.COLLECTING_DATA;
  const isAnalysisDone = status.value === ProjectStatus.ANALYSIS_DONE;

  const isEditMode =
    status.value === ProjectStatus.OFFER_DRAFT || isAnalysisDone;

  const isConfrimEnable =
    areActionsDisabled || status.value === ProjectStatus.OFFER_DRAFT;

  const isProjectCompleted =
    status.value === ProjectStatus.FINISHED ||
    status.value === ProjectStatus.OFFER_ARCHIVED;
  const isCollectingData = status.value === ProjectStatus.COLLECTING_DATA;

  const routeToCollectData = params.codename
    ? `/admin/organizations/${params.codename}/projects/${project.id}/collect-data/`
    : `/project/${project.id}/collect-data/`;
  const routeToReport = params.codename
    ? `/admin/organizations/${params.codename}/projects/${project.id}/reports/`
    : `/project/${project.id}/reports/`;

  const isCustomerSatisfactionProject =
    product_slug === productsSlugs.CUSTOMER_SATISFACTION;

  const isBexioProject = product_slug === productsSlugs.BEXIO_SATISFACTION;

  const renderSidebar = () => {
    if (isCustomerSatisfactionProject) {
      return (
        <CustomerSatisfaction initialValues={initialValues} status={status} />
      );
    } else if (isBexioProject) {
      return (
        <BexioSidebar
          subscription={subscription}
          initialValues={initialValues}
          packageInfo={project.package}
          withPaymentSection={showPaymentSection}
          status={status}
        />
      );
    } else {
      return (
        <Sidebar
          organization={organization}
          goToPayments={() => history.push('/payments')}
          withPayments={!isBrandValuatorProduct}
          hasSufficientCredits={hasSufficientCredits}
          hasDataCollectionStarted={
            project.status.value === ProjectStatus.COLLECTING_DATA
          }
          applyVoucher={applyVoucher}
          removeVoucher={removeVoucher}
        />
      );
    }
  };

  const overviewUrl = params.codename
    ? `/admin/organizations/${params.codename}/projects/`
    : `/`;

  return (
    <StyledDashboard
      title={project.name || t('page.project', 'Project details')}
      location={location}
      isFullWidth
      combinedTitle={project.combinedTitle}
    >
      {!isBexioUser && (
        <BackTo onClick={() => history.push(overviewUrl)}>
          {t('project.backTo.overview', 'Back to overview')}
        </BackTo>
      )}

      {isJsonEmpty ? (
        <LoaderWrapper>
          <CircularProgress size={80} color="primary" />
        </LoaderWrapper>
      ) : (
        <>
          <ProjectMenuWrapper>
            <ProjectMenu
              id={id}
              projectStatus={status}
              isProjectSubscribable={is_subscribable}
              isProjectLoaded={project.id !== -1}
            />
          </ProjectMenuWrapper>
          <Content>
            <SurveyInstance
              json={product.briefing_template.json || {}}
              initialValues={initialValues}
              onComplete={({ data }: any) => updateProjectData(data)}
              withSidebar
              mode={mode}
              locale={locale}
              showProgressBar="off"
              isSinglePage
              showNavigationButtons="off"
              fixedNavigation={false}
              disableNavigation={true}
              showCompletedPage={false}
              previewMode={{
                isActive: true,
                changeToEditMode: () => updateProjectStatus('edit'),
                cancelEditing: () => updateProjectData(initialValues),
                areActionsDisabled: areActionsDisabled,
                isEditMode: !isEditMode,
                isProcessing: isProcessing,
                isConfirmPossible: isConfrimEnable,
                i18n: {
                  editText: t('project.briefing.header.edit', 'Edit'),
                  saveText: t('project.briefing.header.save', 'Save Changes'),
                  cancelText: t('pages.project.modal.cancel', 'Cancel'),
                },
              }}
              summaryClass={{
                height: '100%',
                padding: '2rem',
                borderLeft: '1px solid #E6E6E5',
              }}
              summaryComponent={
                !isBrandValuatorProduct ? renderSidebar() : null
              }
              header={
                <Header
                  surveyUuid={surveyUuid}
                  updateProjectStatus={updateProjectStatus}
                  status={status}
                  isProcessing={isProcessing}
                  updateValues={(data: any) => setInitialValues(data)}
                  isBexioUser={isBexioUser}
                  projectId={project.id}
                  projectUuid={project.uuid}
                  organization={params.codename || organization.codename}
                  routePush={
                    isBrandValuatorProduct ||
                    isProjectCompleted ||
                    isCollectingData
                      ? routeToReport
                      : routeToCollectData
                  }
                  isBrandValuatorProduct={isBrandValuatorProduct}
                  generateReport={generateReport}
                  history={history}
                  isGeneratingReport={isGeneratingReport}
                  isSubscribableProject={is_subscribable}
                />
              }
            />
          </Content>
        </>
      )}
    </StyledDashboard>
  );
};

export default compose(
  connect(
    (state) => ({
      organization: selectors.authSelectors.userOrganization(state),
      isBexioUser: selectors.authSelectors.isBexioUser(state),
      isBrandValuatorProduct: selectors.products.isBrandValuatorProduct(state),
      user: selectors.authSelectors.userProfile(state),
    }),
    {
      updateProject: actions.projects.setProject,
    }
  ),
  withMetaDescription({ title: 'Dashboard projects' }),
  withProject(({ match, organization }: ProjectProps) => ({
    projectId: match.params.id,
    organizationCodename: match.params.codename || organization.codename,
  }))
)(ProjectEdit);
